/**
 * The automations-catalog side of a role template: which activities a role
 * runs, and the text a search should match against.
 *
 * Names come from `automation-names.json`, a slice of the automations catalog,
 * which is too large to load while browsing roles. `constants.test.ts` fails if
 * the slice drifts from the catalog.
 */

import AUTOMATION_NAMES from './agent-templates/automation-names.json';

import type { TPlannedAutomation } from './planned-automations';
import type { ITemplateCard } from './constants';

/** An automations-catalog activity a role runs. */
export interface IRoleAutomation {
  id: string;
  name: string;
}

const NAME_BY_ID: Readonly<Record<string, string | undefined>> =
  AUTOMATION_NAMES;

/**
 * The activities a role runs, in the order its card lists them. Unknown ids
 * are skipped rather than thrown on: the preview should still render, and the
 * test suite is where a bad id fails.
 */
export function getTemplateAutomations(card: ITemplateCard): IRoleAutomation[] {
  return card.automations.flatMap(id => {
    const name = NAME_BY_ID[id];
    return name ? [{ id, name }] : [];
  });
}

const searchTextCache = new WeakMap<ITemplateCard, string>();

/**
 * Lowercased text a search matches a role against: its title, blurb, other
 * titles, keywords, and the names of the automations it runs (so "three-way
 * match" finds the AP Specialist). Team names are translated, so callers match
 * those themselves. Deliberately not the instructions, which aren't loaded
 * while browsing.
 */
export function getTemplateSearchText(card: ITemplateCard): string {
  const cached = searchTextCache.get(card);
  if (cached !== undefined) return cached;
  const text = [
    card.title,
    card.summary,
    card.alsoKnownAs.join(' '),
    card.keywords.join(' '),
    getTemplateAutomations(card)
      .map(automation => automation.name)
      .join(' '),
  ]
    .join(' ')
    .toLowerCase();
  searchTextCache.set(card, text);
  return text;
}

/**
 * The plan a role template starts an agent with: every automation the role
 * runs, none of them built yet.
 *
 * @param card - The role template
 * @returns One entry per automation the role runs
 */
export function plannedAutomationsForTemplate(
  card: ITemplateCard,
): TPlannedAutomation[] {
  return getTemplateAutomations(card).map(automation => ({
    activityId: automation.id,
    name: automation.name,
  }));
}
